<script setup lang="ts">
import { computed } from "vue";
import useAwards from "@/composables/use-awards";

const { instances, refresh: refreshAwards } = useAwards();

refreshAwards();

const instance1 = computed(() => {
  if (instances.value[0]?.isDraft) {
    return instances.value[1];
  }
  return instances.value[0];
});

const instance2 = computed(() => {
  if (instances.value[0]?.isDraft) {
    return instances.value[2];
  }
  return instances.value[1];
});
</script>

<template>
  <Card class="w-full max-w-lg">
    <h2 class="pb-4">Statistics</h2>
    <table class="w-full table-auto">
      <thead>
        <tr>
          <th class="w-1/2 py-2 text-left">Nomination Stage</th>
          <th class="w-1/4 py-2 text-right">
            <span v-if="instance1">{{ instance1.awardYear }}</span>
          </th>
          <th class="w-1/4 py-2 text-right">
            <span v-if="instance2">{{ instance2.awardYear }}</span>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td class="w-1/2 py-2">Nominations</td>
          <td class="w-1/4 py-2 text-right">
            <span v-if="instance1">
              {{ instance1.nominationsCount }}
            </span>
          </td>
          <td class="w-1/4 py-2 text-right">
            <span v-if="instance2">
              {{ instance2.nominationsCount }}
            </span>
          </td>
        </tr>
        <tr>
          <td class="w-1/2 py-2">Participating contacts</td>
          <td class="w-1/4 py-2 text-right">
            <span v-if="instance1">
              {{ instance1.participatingContactsNominationStageCount }}
            </span>
          </td>
          <td class="w-1/4 py-2 text-right">
            <span v-if="instance2">
              {{ instance2.participatingContactsNominationStageCount }}
            </span>
          </td>
        </tr>
        <tr>
          <td class="w-1/2 py-2">Candidates</td>
          <td class="w-1/4 py-2 text-right">
            <span v-if="instance1">{{ instance1.candidatesCount ?? "-" }}</span>
          </td>
          <td class="w-1/4 py-2 text-right">
            <span v-if="instance2">{{ instance2.candidatesCount ?? "-" }}</span>
          </td>
        </tr>
        <tr>
          <td class="w-1/2 py-2">Categories with nominations</td>
          <td class="w-1/4 py-2 text-right">
            <span v-if="instance1">
              {{ instance1.categoriesWithNominationsCount }}
            </span>
          </td>
          <td class="w-1/4 py-2 text-right">
            <span v-if="instance2">
              {{ instance2.categoriesWithNominationsCount }}
            </span>
          </td>
        </tr>
      </tbody>
    </table>
    <table class="w-full table-auto">
      <thead>
        <tr>
          <th class="w-1/2 py-2 text-left">Voting Stage</th>
          <th class="w-1/4 py-2 text-right">
            <span v-if="instance1">{{ instance1.awardYear }}</span>
          </th>
          <th class="w-1/4 py-2 text-right">
            <span v-if="instance2">{{ instance2.awardYear }}</span>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td class="w-1/2 py-2">Votes</td>
          <td class="w-1/4 py-2 text-right">
            <span v-if="instance1">{{ instance1.votesCount }}</span>
          </td>
          <td class="w-1/4 py-2 text-right">
            <span v-if="instance2">{{ instance2.votesCount }}</span>
          </td>
        </tr>
        <tr>
          <td class="w-1/2 py-2">Participating contacts</td>
          <td class="w-1/4 py-2 text-right">
            <span v-if="instance1">
              {{ instance1.participatingContactsVotingStageCount }}
            </span>
          </td>
          <td class="w-1/4 py-2 text-right">
            <span v-if="instance2">
              {{ instance2.participatingContactsVotingStageCount }}
            </span>
          </td>
        </tr>
        <tr>
          <td class="w-1/2 py-2">Nominees</td>
          <td class="w-1/4 py-2 text-right">
            <span v-if="instance1">
              {{ instance1.finalizedNomineesCount ?? "-" }}
            </span>
          </td>
          <td class="w-1/4 py-2 text-right">
            <span v-if="instance2">
              {{ instance2.finalizedNomineesCount ?? "-" }}
            </span>
          </td>
        </tr>
        <tr>
          <td class="w-1/2 py-2">Locations</td>
          <td class="w-1/4 py-2 text-right">
            <span v-if="instance1">
              {{ instance1.locationStats?.verifiedLocations ?? "-" }}
            </span>
          </td>
          <td class="w-1/4 py-2 text-right">
            <span v-if="instance2">
              {{ instance2.locationStats?.verifiedLocations ?? "-" }}
            </span>
          </td>
        </tr>
        <tr>
          <td class="w-1/2 py-2">Claimed nominations</td>
          <td class="w-1/4 py-2 text-right">
            <span v-if="instance1">
              {{ instance1.claimedNominationsCount }}
            </span>
          </td>
          <td class="w-1/4 py-2 text-right">
            <span v-if="instance2">
              {{ instance2.claimedNominationsCount }}
            </span>
          </td>
        </tr>
        <tr>
          <td class="w-1/2 py-2">Categories</td>
          <td class="w-1/4 py-2 text-right">
            <span v-if="instance1">
              {{ instance1.finalizedCategoriesCount }}
            </span>
          </td>
          <td class="w-1/4 py-2 text-right">
            <span v-if="instance2">
              {{ instance2.finalizedCategoriesCount }}
            </span>
          </td>
        </tr>
        <tr>
          <td class="w-1/2 py-2">Categories without votes</td>
          <td class="w-1/4 py-2 text-right">
            <span v-if="instance1">
              {{ instance1.categoriesWithoutVotesCount }}
            </span>
          </td>
          <td class="w-1/4 py-2 text-right">
            <span v-if="instance2">
              {{ instance2.categoriesWithoutVotesCount }}
            </span>
          </td>
        </tr>
        <tr>
          <td class="w-1/2 py-2">Reviews</td>
          <td class="w-1/4 py-2 text-right">
            <span v-if="instance1">{{ instance1.reviewsCount || "-" }}</span>
          </td>
          <td class="w-1/4 py-2 text-right">
            <span v-if="instance2">{{ instance2.reviewsCount || "-" }}</span>
          </td>
        </tr>
        <tr>
          <td class="w-1/2 py-2">Offer Page Views</td>
          <td class="w-1/4 py-2 text-right">
            <span v-if="instance1">{{
              instance1.impressionGroupsCount || 0
            }}</span>
          </td>
          <td class="w-1/4 py-2 text-right">
            <span v-if="instance2">{{
              instance2.impressionGroupsCount || 0
            }}</span>
          </td>
        </tr>
        <tr>
          <td class="w-1/2 py-2">Leads</td>
          <td class="w-1/4 py-2 text-right">
            <span v-if="instance1">{{
              instance1.claimedOffersCount || 0
            }}</span>
          </td>
          <td class="w-1/4 py-2 text-right">
            <span v-if="instance2">{{
              instance2.claimedOffersCount || 0
            }}</span>
          </td>
        </tr>
      </tbody>
    </table>
  </Card>
</template>
